import Phaser from 'phaser';
import { Player } from '../../classes/Player';
import { Portal } from 'src/classes/Portal';
import UIScene from 'src/classes/UI/UIScene';
import VirtualJoyStick from 'phaser3-rex-plugins/plugins/virtualjoystick';
import { DropItem } from 'src/classes/DropItem';
import { Character } from 'src/classes/Character';
import { GameObjectAttributes } from 'src/classes/CustomAttributes';
import { name_to_attributes } from 'src/classes/dataClasses/Mappings';
import { Creature } from 'src/classes/Creature';
import { Memory } from 'src/classes/Memories';
import { StoryItem } from 'src/classes/StoryItem';
import { KeyItem } from 'src/classes/Keys';
import { Inventory } from 'src/classes/UI/Inventory';
import { Hazard, consequence } from 'src/classes/Hazard';
import { GlobalService } from '../services/global.service';
import { WebsocketService } from '../services/websocket.service';
import { PlayerMetadata } from 'src/classes/PlayerMetadata';
import { Peer } from 'src/classes/Peer';
import { ElectricArc } from 'src/classes/ElectricArc';
import { Bullet } from 'src/classes/SlingShot';
import MainScene from './MainScene';

export interface OverlayCollisionCallback {
  (tile: Phaser.Tilemaps.Tile, collisionData: Phaser.Types.Physics.Matter.MatterCollisionData): boolean;
}

export interface TweenPoint {
  x: number;
  y: number;
  characterName: string;
  durationMs: number;
}

export default class BaseLevelScene extends Phaser.Scene {
  tileWidth: integer;
  tileHeight: integer;
  portals: Portal[] = [];
  player: Player;
  joystick: VirtualJoyStick;
  map: Phaser.Tilemaps.Tilemap;
  animatedTiles: any;
  bodies: MatterJS.BodiesFactory;
  activeCreatures: Creature[] = [];
  playerScale: number = 1;
  overlay: Phaser.Tilemaps.TilemapLayer;
  storyItems: StoryItem[] = [];
  dropItems: DropItem[] = [];
  characters: Character[] = [];
  electricArc: Array<ElectricArc> = [];
  electricArcZones: Phaser.GameObjects.Zone[] = [];
  electricArcHurtCooldown: boolean = false;
  electricArcTimer: Phaser.Time.TimerEvent | null = null;
  electricArcSensors: MatterJS.BodyType[] = [];
  static previousSceneKey = "mainScene";
  static skipUpdate = false;
  public compassAngle = 0;
  otherPlayers: Phaser.GameObjects.Group;
  public playerMetadata: PlayerMetadata = new PlayerMetadata('default');
  public tweenPoints: TweenPoint[] = [];
  bossWasDisplayed: boolean = false;

  constructor(key: string) {
    super({ key: key });
  }

  preloadBaseLevel() {

    // Load the plugin
    // https://raw.githubusercontent.com/nkholski/phaser-animated-tiles/master/dist/AnimatedTiles.js
    this.load.scenePlugin('AnimatedTiles', 'assets/externalScripts/AnimatedTiles.js', 'animatedTiles', 'animatedTiles');

    // Load character data
    Character.preload(this);

    // Load portal assets
    Portal.preload(this);
  }

    removeTile(tile: Phaser.Tilemaps.Tile, tileBody: Phaser.GameObjects.GameObject){
      this.overlay.removeTileAt(tile.x, tile.y);
      // tileBody.removeBody();
      tileBody.destroy();
    }
  
    hitOverlay (tile: Phaser.Tilemaps.Tile, collisionData: Phaser.Types.Physics.Matter.MatterCollisionData): boolean
    {
      if(collisionData.bodyA.label === "bulletCollider"){
        const bulletBody: Bullet = collisionData.bodyA.gameObject as Bullet;
        bulletBody.disableBullet();
        const tileBody = collisionData.bodyB.gameObject;//  as Phaser.Physics.Matter.TileBody; 
        this.removeTile(tile, tileBody);
      }
      else if(collisionData.bodyB.label === "bulletCollider"){
        const bulletBody: Bullet = collisionData.bodyB.gameObject as Bullet;
        bulletBody.disableBullet();
        const tileBody = collisionData.bodyA.gameObject;
        this.removeTile(tile, tileBody);
      }
      // Return true to exit processing collision of this tile vs the sprite - in this case, it
      // doesn't matter since the coin ti      this.heartbeatSound.stop();les are not set to collide.
      return false;
    }

  createBaseLevel(playerScale: number = 1, cameraZoom: number = 1) {
    this.playerScale = playerScale;
    this.bodies = new Phaser.Physics.Matter.MatterPhysics(this).bodies;

    // Animate the tiles
    this.animatedTiles.init(this.map);

    // Create a player
    this.player = new Player({
      scene: this,
      x: 0,
      y: 0,
      texture: 'player',
    }, playerScale);


    // Make camera follow player
    this.cameras.main.setZoom(cameraZoom*GlobalService.zoom);
    this.cameras.main.startFollow(this.player, true, 0.05, 0.05);
    this.configureCameraForJoystick();

    // Add interactive objects
    this.addAllObjects();

    // Attach joystick to the player
    this.joystick = (
      this.scene.manager.getScene('UIScene') as UIScene
    ).customJoystick.joystick;
    this.player.cursors =
      this.joystick.createCursorKeys() as Phaser.Types.Input.Keyboard.CursorKeys;

    // Add other players for online
    this.otherPlayers = this.add.group();
  }

  fadeSceneIn(){
    //  Get a random color
    const red = Phaser.Math.Between(50, 255);
    const green = Phaser.Math.Between(50, 255);
    const blue = Phaser.Math.Between(50, 255);
    this.cameras.main.fadeFrom(2000, red, green, blue);
  }

  addAllObjects(){
    // Add interactive objects
    this.addObjects("Passages");
    this.addObjects("Photos");
    this.addObjects("Items");
    this.addObjects("Creatures");
    this.addObjects("Keys");
    this.addObjects("Hazards");
    this.addObjects("Effects");
  }

  clearCreatureTimers(){
    this.activeCreatures.forEach((c) => {
      clearInterval(c.wanderTimer);
      clearInterval(c.attackTimer);
    });
  }

  removeItems(){
    this.dropItems.forEach((i) => {i.destroy(true)});
    this.dropItems = [];
  }

  removeAllObjects(){
    this.activeCreatures.forEach((c) => {c.destroy(true)});
    this.portals.forEach((p) => {p.destroy(true)});
    this.activeCreatures = [];
    this.portals = [];
    this.removeItems();
  }

  clearPeers() {
    console.log("Clearing all peers from exited scene");
    if (this.otherPlayers && this.otherPlayers.getChildren()) {
      this.otherPlayers.getChildren().forEach((peer: Peer) => {
        peer.customDestroy();
        this.otherPlayers.remove(peer, true, true);
      });
    }
  }

  resetLevel(){
    BaseLevelScene.skipUpdate = true;
    this.clearCreatureTimers();
    this.removeAllObjects();
    this.clearPeers();
    const x = this.player.x;
    const y = this.player.y;
    this.scene.restart().manager.update(1,1);
    this.scene.scene.cameras.main.fadeIn(10000, 110);
    setTimeout(() => {
      this.scene.switch('mainScene');
      if(WebsocketService.socket && WebsocketService.socket.readyState === WebSocket.OPEN){
        console.log('Update players level globally');

        // Update the players level globally.
        MainScene.playerMetadata.levelName = 'mainScene';
        MainScene.playerMetadata.lastUpdated = Date.now();
        WebsocketService.updatePlayer(MainScene.playerMetadata as PlayerMetadata);
      }
      this.scene.get('mainScene').cameras.main.fadeIn(1000, 100);
      BaseLevelScene.skipUpdate = false;
    }, 250);
  }

  addObjects(objectLayerName: string) {
    try{
      const resources = this.map.getObjectLayer(objectLayerName);
      resources.objects.forEach((resource) => {
        const attributes: GameObjectAttributes = name_to_attributes.get(resource.name);
        if(resource.type === "Creature"){
          try {
           const token = resource.properties?.find(prop => prop.name === "token")?.value as string ?? null;
           const creatureScale = resource.properties?.find(prop => prop.name === "scale")?.value as number ?? 1;
           const initiallyDisabled = resource.properties?.find(prop => prop.name === "initiallyDisabled")?.value as boolean ?? false;
           const deathMusic = resource.properties?.find(prop => prop.name === "deathMusic")?.value as string ?? null;

           console.log(`Adding creature ${resource.name} at (${resource.x}, ${resource.y}) with scale ${creatureScale} and initiallyDisabled=${initiallyDisabled}`);
            const creature = new Creature(
              {
                scene: this,
                x: resource.x,
                y: resource.y,
                texture: resource.name,
              },
              this.bodies,
              attributes.aggressive,
              this.playerScale*creatureScale,
              attributes.allowRotation,
              token,
              deathMusic
            );
            creature.setCreatureEnabled(!initiallyDisabled);
            this.activeCreatures.push(creature);
          }
          catch{
            console.error(`Error generating ${resource.name}`);
          }
        }
        else if (resource.type === "Character"){
          // extract character properties
          const dialog: string = resource.properties.find(prop => prop.name === "dialog").value as string;
          const url: string = resource.properties.find(prop => prop.name === "url").value as string;
          const disablingToken = resource.properties.find(prop => prop.name === "disabledByToken")?.value as string;
          const tokenJson = resource.properties.find(prop => prop.name === "token");
          const token = tokenJson ? tokenJson.value as string : null;
          const scaleJson = resource.properties.find(prop => prop.name === "scale");
          const scale = scaleJson ? scaleJson.value as number : 1;
          const disabledByToken = disablingToken ? MainScene.tokens.includes(disablingToken) : false;
          // Add any characters that aren't disabled.
          if(!disabledByToken){
            const character = new Character(
              {
                scene: this,
                x: resource.x + resource.width / 2,
                y: resource.y - resource.height,
                texture: resource.name,
              },
              `${resource.name}_idle`,
              resource.name,
              dialog,
              url,
              this.playerScale,
              scale,
              undefined,
              token
              );
            // character.setActive(false);
            // character.setVisible(false);
            this.characters.push(this.add.existing(character));
          }
        }
        else if(resource.type == "Passage"){
          try {
            const playerOffsetX: number = this.playerScale*resource.properties.find(prop => prop.name === "playerOffsetX").value as number;
            const playerOffsetY: number = this.playerScale*resource.properties.find(prop => prop.name === "playerOffsetY").value as number;
            const requiresKey: boolean = resource.properties.find(prop => prop.name === "requiresKey")?.value as boolean;
            this.portals.push(
              new Portal(
                {
                  scene: this,
                  x: resource.x + resource.width/2,
                  y: resource.y + resource.height/2,
                  width: resource.width,
                  height: resource.height,
                  texture: 'catacomb_exit',
                  portalName: resource.name,
                  prerequisites: null,
                  requiresKey: requiresKey,
                },
                this.playerScale,
                false,
                playerOffsetX,
                playerOffsetY
              )
            );
            if(resource.name === BaseLevelScene.previousSceneKey){
              this.player.x = resource.x + playerOffsetX;
              this.player.y = resource.y + playerOffsetY;
            }
          }
          catch(e){
            console.error(`Error generating ${resource.name}`, e);
          }
        }
        else if(resource.type === "Photo"){
          try {
            new Memory(this, resource.x, resource.y, `public/${resource.name}.png`, this.playerScale);
          }
          catch{
            console.error(`Error placing photo ${resource.name}`);
          }
        }
        else if(resource.type === 'Item'){
          const item = new DropItem({
            scene: this,
            x: resource.x,
            y: resource.y,
            frame: resource.name,
          });
          if(resource.name === 'heart'){
            item.setScale(0.4);
          }
          item.setStatic(true);
          this.add.existing(item);
        }
        else if(resource.type == 'StoryItem'){
          // extract story item properties
          const texture: string = resource.properties.find(prop => prop.name === "texture").value as string;
          const animationKey: string = resource.properties.find(prop => prop.name === "animationKey").value as string;
          const storyItem = new StoryItem({
            scene: this,
            x: resource.x,
            y: resource.y,
            texture: texture,
            animationKey: animationKey,
            name: resource.name,
          },
          this.playerScale);
          storyItem.setStatic(true);
          this.add.existing(storyItem);
          this.storyItems.push(storyItem);
        }
        else if(resource.type == 'DropItem'){
          // extract drop item properties
          const name: string = resource.name;
          const dropItem = new DropItem({
            scene: this,
            x: resource.x,
            y: resource.y,
            frame: name,
          },
          this.playerScale);
          dropItem.setStatic(true);
          this.add.existing(dropItem);
          this.dropItems.push(dropItem);
        }
        else if (resource.type === "KeyItem"){

          // determine what passages this key unlocks
          const unlocksString = resource.properties.find(prop => prop.name === "unlocks").value as string;
          const unlocks = unlocksString.split(',')

          // check that the inventory key doesn't already contain these passages
          if(!Inventory.containsAllPassages(unlocks)){
            // if not, add the key to the scene
            const keyItem = new KeyItem({
              scene: this,
              x: resource.x,
              y: resource.y,
              unlocks: unlocks,
            },
            this.playerScale);
          }
        }
        else if (resource.type === "Hazard"){
          const consequence = resource.properties.find(prop => prop.name === "consequence").value as consequence;
          const hazard = new Hazard({
            scene: this,
            x: resource.x + resource.width/2,
            y: resource.y + resource.height/2,
            width: resource.width,
            height: resource.height,
            consequence: consequence
          },
          this.playerScale);
        }
        else if (resource.type === "Electricity"){
          const sequenceNumber = resource.properties.find(prop => prop.name === "sequenceNumber").value as number;
          
          this.electricArc.push({
            x: resource.x,
            y: resource.y,
            sequenceNumber: sequenceNumber,
            delayMs: 500
          });
        }
        else if( resource.type === "TweenPoint"){
          console.log("Adding tween point:", resource);
          // Tween points are used by creatures to define paths of movement
          this.tweenPoints.push({
            x: resource.x,
            y: resource.y,
            characterName: resource.properties.find(prop => prop.name === "characterName").value as string,
            durationMs: resource.properties.find(prop => prop.name === "durationMs").value as number
          });
        }
      });
    }
    catch(e){
      console.error(`Unable to add objects from ${objectLayerName}`, e)
    }
  }

  createOverlayLayer(map: Phaser.Tilemaps.Tilemap, tileset: Phaser.Tilemaps.Tileset, overlayCollisionCallback: OverlayCollisionCallback){
    // add overlay for hiding stuff
    this.overlay = map.createLayer('Overlay', tileset);
    const overlayData = this.overlay.layer.data;
    for(let i=0; i<overlayData.length; i++){
      const row = overlayData[i];
      for(let j=0; j<row.length; j++){
        const tile = row[j];
        if(tile && tile.index >= 0){
          var tileBody = new Phaser.Physics.Matter.TileBody(this.matter.world, tile);
          const shouldCollide: boolean = tile?.properties["collides"] as boolean;
          if(!shouldCollide){
            tileBody.setSensor(true);
          }
          tileBody.setOnCollide(overlayCollisionCallback.bind(this, tile));
        }
      }
    }
    this.matter.world.convertTilemapLayer(this.overlay);
  }

  async update(time: number, delta: number) {

    WebsocketService.scene = this.scene.scene as BaseLevelScene;
    if(! BaseLevelScene.skipUpdate){
      // every 16ms. Game logic here
      this.player.update(this.joystick);

      this.activeCreatures = this.activeCreatures.filter((ac) => {
        return ac !== undefined;
      });

      this.activeCreatures.forEach((c) => {
        if (c !== undefined && c !== null) {
          c.update();
        }
      });

      if(BaseLevelScene.previousSceneKey !== this.scene.key && this.portals.length > 0){
        const spawnPortal: Portal = this.portals.find((portal: Portal) => {
          return portal.portalName === BaseLevelScene.previousSceneKey;
        });
        if(spawnPortal){
          this.player.x = spawnPortal.x + spawnPortal.playerOffsetX*this.playerScale;
          this.player.y = spawnPortal.y + spawnPortal.playerOffsetY*this.playerScale;
        }
        else{
          console.error("Error: no spawnPortal found");
        }
        BaseLevelScene.previousSceneKey = this.scene.key;
      }
    }

    if (this.playerMetadata.update(this.player)) {
      WebsocketService.updatePlayer(this.playerMetadata);
    }
  }

  animateElectricArc(){
    console.log("Creating electric arc");
    console.log(this.electricArc);
    ElectricArc.animateElectricArc(this, this.electricArc, 10000, { color: 0x00ffff, thickness: 2, jaggedness: 10, alpha: 0.8, flickerMs: 100 });
    this.createElectricArcSensors();
    // Start periodic reappearance if not already started
    if (!this.electricArcTimer) {
      this.electricArcTimer = this.time.addEvent({
        delay: 15000, // 15 seconds between reappearances (adjust as needed)
        loop: true,
        callback: () => {
          ElectricArc.animateElectricArc(this, this.electricArc, 10000, { color: 0x00ffff, thickness: 2, jaggedness: 10, alpha: 0.8, flickerMs: 100 });
          this.createElectricArcSensors();
        }
      });
    }
  }

  createElectricArcSensors() {
    // Remove old sensors
    if (this.electricArcSensors) {
      this.electricArcSensors.forEach(sensor => this.matter.world.remove(sensor));
      this.electricArcSensors = [];
    }
    // For each segment between arc points, create a Matter.js sensor
    const sortedArcs = this.electricArc.slice().sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    for (let i = 0; i < sortedArcs.length - 1; i++) {
      const start = sortedArcs[i];
      const end = sortedArcs[i + 1];
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;
      const width = Phaser.Math.Distance.Between(start.x, start.y, end.x, end.y);
      const angle = Phaser.Math.Angle.Between(start.x, start.y, end.x, end.y);
      const sensor = this.matter.add.rectangle(midX, midY, width, 20, {
        isSensor: true,
        // isStatic: true,
        label: 'electricArcSensor',
        angle: angle
      });
      this.electricArcSensors.push(sensor);
    }
    
    // Remove sensors after arc duration
    this.time.delayedCall(10000, () => {
      this.electricArcSensors.forEach(sensor => this.matter.world.remove(sensor));
      this.electricArcSensors = [];
    });
  }

  tweenCharactersAlongPoints(){
    console.log("Tweening characters along points:", this.tweenPoints);
    this.tweenPoints.forEach((tp) => {
      console.log(`Starting tween for ${tp.characterName} to (${tp.x}, ${tp.y}) over ${tp.durationMs}ms`);
      const targetCharacter = this.scene.scene.children.getByName(tp.characterName) as Character;
      console.log(targetCharacter);
      if(targetCharacter){
        this.tweens.add({
          targets: targetCharacter,
          x: tp.x,
          y: tp.y,
          ease: 'Linear',
          duration: tp.durationMs,
          repeat: 0,
          yoyo: false,
          onComplete: () => {
            if(MainScene.tokens.includes("VCU Engineering Building")){return;}
            console.log(`Completed tween for ${tp.characterName}`);
            this.animateElectricArc();
            if(!this.bossWasDisplayed){
              this.replaceNateWithSkeleton();
            }
            this.bossWasDisplayed = true;
          }
        });
      }
    });
  }

  configureCameraForJoystick() {
    let { width, height } = this.sys.game.canvas;
    this.cameras.main.width = width;
    this.cameras.main.height = height;
    this.cameras.main.x = 0;
    this.cameras.main.y = 0;
  }

  /**
   * Removes the character named 'Nate' and adds/activates the skeleton creature.
   */
  replaceNateWithSkeleton() {
    // Remove Nate character
    const nateIdx = this.characters.findIndex(c => c.name === 'Nate');
    if (nateIdx !== -1) {
      const nateChar = this.characters[nateIdx];
      if (nateChar && nateChar.scene) {
        nateChar.setActive(false);
        nateChar.setVisible(false);
        nateChar.setCollidesWith(0); // Disable all collisions
      }
      // this.characters.splice(nateIdx, 1);
    }
    // Find skeleton creature
    const skeleton = this.activeCreatures.find(c => c.name === 'skeleton');
    if (skeleton) {
      if (!skeleton.scene) {
        this.add.existing(skeleton);
      }
      skeleton.setCreatureEnabled(true);
    }
  }

  // Clean up timer and sensors on shutdown
  shutdown() {
    if (this.electricArcTimer) {
      this.electricArcTimer.remove(false);
      this.electricArcTimer = null;
    }
    if (this.electricArcSensors) {
      this.electricArcSensors.forEach(sensor => this.matter.world.remove(sensor));
      this.electricArcSensors = [];
    }
  }
}
